import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/api";
import { Badge, CAMPAIGN_STATUS, fmtDate, money, ProgressBar, useTable, SortTh, Pager, selectCls } from "./shared";
import { CampaignDataPanel } from "./CampaignDataPanel";
import { COST_COLS, chf } from "./MarginsPanel";

const CostCell = ({ c, cost }) => {
  if (!cost) return <span className="text-[#6b7280] font-mono text-xs">—</span>;
  const total = COST_COLS.reduce((a, [k]) => a + cost.costs[k].chf, 0);
  return (
    <div data-testid={`mc-cost-${c.id}`} className="font-mono text-[11px] leading-4"
      title={COST_COLS.map(([k, label]) => `${label} ${chf(cost.costs[k].chf)}`).join(" · ")}>
      <div className="text-[#6b7280]">Costi {chf(total)}</div>
      <div data-testid={`mc-margin-${c.id}`} className={cost.margin_chf < 0 ? "text-[#E3242B] font-semibold" : "text-[#10b981] font-semibold"}>
        Margine {chf(cost.margin_chf)}{cost.margin_pct != null ? ` · ${cost.margin_pct}%` : ""}
      </div>
    </div>
  );
};

export const ManagedCampaignsPanel = () => {
  const [data, setData] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [costs, setCosts] = useState({});
  const load = useCallback(() => {
    adminApi.managedList().then((d) => setData(d.campaigns)).catch(() => toast.error("Errore caricamento campagne"));
    adminApi.managedAlerts().then((d) => setAlerts(d.alerts)).catch(() => {});
    adminApi.margins().then((d) => setCosts(Object.fromEntries(d.campaigns.map((r) => [r.id, r])))).catch(() => {});
  }, []);
  const alertOf = (id) => alerts.find((a) => a.id === id);
  useEffect(() => { load(); }, [load]);

  const filterFn = useCallback((c) => (!status || c.status === status) &&
    (!q || `${c.client_email} ${c.landing_url} ${c.params?.skill} ${c.params?.area}`.toLowerCase().includes(q.toLowerCase())), [q, status]);
  const table = useTable(data, { filterFn, initialSort: { key: "created_at", dir: "desc" } });

  if (openId) return <CampaignDataPanel id={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div data-testid="admin-managed-campaigns" className="space-y-4">
      {alerts.length > 0 && (
        <div data-testid="mc-alerts" className="border border-[#E0262C] bg-[#2a0d0e] divide-y divide-[#4a1517]">
          {alerts.map((a) => (
            <button key={a.id} data-testid={`mc-alert-${a.id}`} onClick={() => setOpenId(a.id)}
              className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-[#3a1012] transition-colors">
              <AlertTriangle className="w-4 h-4 text-[#E0262C] flex-shrink-0" />
              <span className="text-sm text-[#2B3036] mr-auto">
                <strong>{a.params?.skill} · {a.params?.area}</strong>{" "}
                {a.level === "overdue" ? "— i 7 giorni sono terminati: chiudi la campagna" : "— è entrata nel 7° giorno (ultimo giorno)"}
              </span>
              <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${a.level === "overdue" ? "bg-[#E0262C] text-white" : "border border-[#E0262C] text-[#E0262C]"}`}>
                {a.level === "overdue" ? "Scaduta" : "Ultimo giorno"}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Input data-testid="mc-search" placeholder="Cerca cliente, sito, skill, area..." value={q} onChange={(e) => setQ(e.target.value)}
          className="h-10 w-72 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
        <select data-testid="mc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
          <option value="">Tutti gli stati</option>
          {["awaiting_start", "running", "paused", "completed", "refunded"].map((s) => <option key={s} value={s}>{CAMPAIGN_STATUS[s].label}</option>)}
        </select>
      </div>
      <div className="border border-[#d5dbde] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              <SortTh label="Cliente" k="client_email" table={table} />
              <th className="px-4 py-3">Richiesta</th>
              <SortTh label="Inizio" k="start_date" table={table} />
              <SortTh label="Fine" k="end_date" table={table} />
              <SortTh label="Rimanenti" k="days_remaining" table={table} />
              <SortTh label="Avanzamento" k="progress" table={table} />
              <SortTh label="Contatti" k="totals.contacts" table={table} />
              <th className="px-4 py-3">Costo dati / Margine</th>
              <SortTh label="Stato" k="status" table={table} />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((c) => (
              <tr key={c.id} data-testid={`mc-row-${c.id}`} onClick={() => setOpenId(c.id)}
                className={`border-t border-[#d5dbde] hover:bg-[#f4f6f7] cursor-pointer ${alertOf(c.id) ? "shadow-[inset_3px_0_0_#E0262C]" : ""}`}>
                <td className="px-4 py-3"><div>{c.client_name}</div><div className="font-mono text-xs text-[#6b7280]">{c.client_email}</div></td>
                <td className="px-4 py-3 max-w-[240px]">
                  <div className="truncate">{c.params?.skill} · <span className="text-[#5b6168]">{c.params?.area}</span></div>
                  <div className="font-mono text-[11px] text-[#6b7280] truncate">{c.landing_url} · {money(c.price, c.currency)} · {c.duration_days}gg</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{fmtDate(c.start_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtDate(c.end_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.start_date ? `${c.days_remaining} gg` : "—"}</td>
                <td className="px-4 py-3 w-40"><ProgressBar value={c.progress} /><div className="text-[10px] font-mono text-[#6b7280] mt-1">{c.progress}% · giorno {c.current_day}/{c.duration_days}</div></td>
                <td className="px-4 py-3 font-display text-base">{c.totals?.contacts ?? 0}</td>
                <td className="px-4 py-3"><CostCell c={c} cost={costs[c.id]} /></td>
                <td className="px-4 py-3"><Badge map={CAMPAIGN_STATUS} value={c.status} testid={`mc-status-${c.id}`} /></td>
              </tr>
            ))}
            {!table.rows.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessuna campagna cliente pagata</td></tr>}
          </tbody>
        </table>
        <Pager table={table} testid="mc-pager" />
      </div>
    </div>
  );
};
